import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class NameFormatService {
  /**
   * Format a value into a human-friendly "First Last" form.
   * - If value is an email (contains @) the local-part is used and split on dots/underscores/dashes.
   * - If value matches "Last, First" or "Last, I (First)" patterns it's converted to "First Last".
   * - Otherwise returns first and last name only when a full name contains middle names.
   */
  public formatName(input: unknown): string {
    const s = (input || '').toString().trim().replace(/\s+/g, ' ');
    if (!s) return '';

    // Email address -> use local-part
    if (s.includes('@')) {
      const local = s.split('@')[0] || '';
      // Replace separators with spaces
      const cleaned = local.replace(/[._\-]+/g, ' ').trim();
      const parts = cleaned.split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '';
      if (parts.length === 1) return this.capitalize(parts[0]);
      return this.formatFirstLast(parts.map((part) => this.capitalize(part)));
    }

    // Pattern: Last, I (First) -> capture last and first-in-parens
    const parenMatch = s.match(/^\s*([^,]+),\s*[^()]*\(([^)]+)\)\s*$/);
    if (parenMatch) {
      const last = parenMatch[1].trim();
      const first = parenMatch[2].trim();
      return this.formatFirstLast([...first.split(/\s+/), last]);
    }

    // Pattern: Last, First
    const commaMatch = s.match(/^\s*([^,]+),\s*(.+)\s*$/);
    if (commaMatch) {
      const last = commaMatch[1].trim();
      const rest = commaMatch[2].trim();
      return this.formatFirstLast([...rest.split(/\s+/), last]);
    }

    return this.formatFirstLast(s.split(/\s+/));
  }

  private formatFirstLast(parts: string[]): string {
    const nameParts = parts.map((part) => part.trim()).filter(Boolean);
    if (nameParts.length <= 2) return nameParts.join(' ');
    return `${nameParts[0]} ${nameParts[nameParts.length - 1]}`;
  }

  private capitalize(word: string): string {
    if (!word) return '';
    const w = word.toString().trim().toLowerCase();
    return w.charAt(0).toUpperCase() + w.slice(1);
  }
}
